// Greater London location data for SEO landing pages.
// Each area has unique copy fields so pages aren't thin or duplicated.

export type LondonArea = {
  slug: string;
  name: string;
  borough: string;
  region: "West" | "East" | "North" | "South" | "Central";
  postcodes: string[]; // outward codes covered
  tubeOrRail: string[]; // notable stations
  intro: string; // 1-2 sentence area sketch
  housing: string; // sentence on typical housing stock (relevant to handyman/TV/assembly)
  landmarks: string[]; // 2-4 well-known landmarks/parks
  neighbours: string[]; // slugs of neighbouring areas
};

export const LONDON_AREAS: LondonArea[] = [
  {
    slug: "ealing",
    name: "Ealing",
    borough: "Ealing",
    region: "West",
    postcodes: ["W5", "W13"],
    tubeOrRail: ["Ealing Broadway", "Ealing Common", "South Ealing"],
    intro: "Ealing is a leafy west London suburb known as the ‘Queen of the Suburbs’, with a mix of Victorian terraces, Edwardian semis and modern apartments around Ealing Broadway.",
    housing: "Plasterboard stud walls in newer flats and solid brick in the older stock — both come up regularly for TV mounting and shelving work.",
    landmarks: ["Walpole Park", "Pitzhanger Manor", "Ealing Common", "Dickens Yard"],
    neighbours: ["acton", "chiswick", "brentford", "hanwell"],
  },
  {
    slug: "chiswick",
    name: "Chiswick",
    borough: "Hounslow",
    region: "West",
    postcodes: ["W4"],
    tubeOrRail: ["Chiswick Park", "Turnham Green", "Gunnersbury"],
    intro: "Chiswick is a riverside west London neighbourhood with elegant period houses around Chiswick High Road and the lively Devonshire Road.",
    housing: "Lots of Victorian conversions and period properties — solid masonry walls, original cornicing and a steady stream of flat-pack and wall-mounting jobs.",
    landmarks: ["Chiswick House & Gardens", "Bedford Park", "Strand on the Green", "Turnham Green"],
    neighbours: ["acton", "ealing", "hammersmith", "brentford"],
  },
  {
    slug: "hammersmith",
    name: "Hammersmith",
    borough: "Hammersmith & Fulham",
    region: "West",
    postcodes: ["W6", "W14"],
    tubeOrRail: ["Hammersmith", "Barons Court", "Ravenscourt Park"],
    intro: "Hammersmith is a busy west London hub on the river with strong transport links and a mix of mansion blocks, riverside flats and Victorian terraces.",
    housing: "Mansion-block flats with thick masonry walls and a high share of rented homes — frequent moves, IKEA assembly and TV mounting work.",
    landmarks: ["Hammersmith Apollo", "Riverside Studios", "Furnival Gardens", "Lyric Theatre"],
    neighbours: ["chiswick", "fulham", "shepherds-bush", "acton"],
  },
  {
    slug: "wembley",
    name: "Wembley",
    borough: "Brent",
    region: "North",
    postcodes: ["HA9", "HA0"],
    tubeOrRail: ["Wembley Park", "Wembley Central", "Wembley Stadium"],
    intro: "Wembley combines the iconic stadium and arena with rapidly growing new-build neighbourhoods like Wembley Park and Quintain.",
    housing: "Lots of new-build apartments with plasterboard partitions — heavy demand for safe TV mounting, blinds and large flat-pack assemblies.",
    landmarks: ["Wembley Stadium", "OVO Arena Wembley", "Boxpark Wembley", "King Edward VII Park"],
    neighbours: ["acton", "ealing", "harrow", "willesden"],
  },
  {
    slug: "acton",
    name: "Acton",
    borough: "Ealing",
    region: "West",
    postcodes: ["W3", "W4"],
    tubeOrRail: ["Acton Town", "East Acton", "Acton Central"],
    intro: "Acton sits between Ealing and Shepherd's Bush and has transformed in recent years with new developments around Acton Main Line and the Elizabeth line.",
    housing: "A mix of Edwardian terraces and brand-new builds — both regular sources of moving help, assembly and small repair jobs.",
    landmarks: ["Acton Park", "Gunnersbury Park", "Acton Market"],
    neighbours: ["ealing", "chiswick", "shepherds-bush", "hammersmith"],
  },
  {
    slug: "richmond",
    name: "Richmond",
    borough: "Richmond upon Thames",
    region: "South",
    postcodes: ["TW9", "TW10"],
    tubeOrRail: ["Richmond", "North Sheen", "St Margarets"],
    intro: "Richmond is a riverside south-west London town with one of the most-loved high streets in London, framed by Richmond Park and the Thames.",
    housing: "Period homes, Georgian terraces and family-sized flats — heritage walls call for careful drilling and experienced handymen.",
    landmarks: ["Richmond Park", "Richmond Green", "Petersham Meadows", "Kew Gardens"],
    neighbours: ["kingston", "twickenham", "putney", "brentford"],
  },
  {
    slug: "kingston",
    name: "Kingston",
    borough: "Kingston upon Thames",
    region: "South",
    postcodes: ["KT1", "KT2"],
    tubeOrRail: ["Kingston", "Norbiton", "Hampton Wick"],
    intro: "Kingston upon Thames is a riverside south-west London town with a thriving shopping centre, a university campus and a steady flow of new residents.",
    housing: "Lots of student flats and family homes around the town centre — high turnover means constant moving, assembly and small repair jobs.",
    landmarks: ["Bentall Centre", "Kingston Bridge", "Canbury Gardens", "Kingston University"],
    neighbours: ["richmond", "wimbledon", "surbiton", "new-malden"],
  },
  {
    slug: "brentford",
    name: "Brentford",
    borough: "Hounslow",
    region: "West",
    postcodes: ["TW8"],
    tubeOrRail: ["Brentford", "Boston Manor", "Gunnersbury"],
    intro: "Brentford has been transformed by the new stadium and waterside developments at Brentford Lock and the Grand Union Canal.",
    housing: "Newly built apartments with thin plasterboard walls — TV mounting needs proper stud-finding and safe fixings.",
    landmarks: ["Gtech Community Stadium", "Syon Park", "Brentford Lock", "Kew Bridge"],
    neighbours: ["chiswick", "ealing", "richmond", "hounslow"],
  },
  {
    slug: "hanwell",
    name: "Hanwell",
    borough: "Ealing",
    region: "West",
    postcodes: ["W7"],
    tubeOrRail: ["Hanwell", "Castle Bar Park", "Drayton Green"],
    intro: "Hanwell is a quiet residential pocket of west London with leafy streets, the Grand Union Canal and the Wharncliffe Viaduct as a backdrop.",
    housing: "Mostly Edwardian and inter-war terraces with traditional solid walls and original features.",
    landmarks: ["Brent Lodge Park", "Hanwell Zoo", "Wharncliffe Viaduct", "Grand Union Canal"],
    neighbours: ["ealing", "acton", "southall", "brentford"],
  },
  {
    slug: "fulham",
    name: "Fulham",
    borough: "Hammersmith & Fulham",
    region: "West",
    postcodes: ["SW6"],
    tubeOrRail: ["Fulham Broadway", "Parsons Green", "Putney Bridge"],
    intro: "Fulham is a riverside village in south-west London known for the Broadway, Parsons Green and Hurlingham Park.",
    housing: "Tightly packed Victorian terraces and mansion-block conversions — common scenes for IKEA assembly and shelf hanging on plaster.",
    landmarks: ["Craven Cottage", "Bishops Park", "Hurlingham Park", "Parsons Green"],
    neighbours: ["hammersmith", "chelsea", "putney", "shepherds-bush"],
  },
  {
    slug: "shepherds-bush",
    name: "Shepherd's Bush",
    borough: "Hammersmith & Fulham",
    region: "West",
    postcodes: ["W12"],
    tubeOrRail: ["Shepherd's Bush", "White City", "Goldhawk Road"],
    intro: "Shepherd's Bush is anchored by Westfield London and the historic Shepherd's Bush Green, with strong demand from young renters and new families.",
    housing: "Mix of new-build flats around White City and older terraced conversions — frequent moves and flat-pack assemblies.",
    landmarks: ["Westfield London", "Shepherd's Bush Green", "BBC Television Centre", "Bush Theatre"],
    neighbours: ["acton", "hammersmith", "kensington", "notting-hill"],
  },
  {
    slug: "putney",
    name: "Putney",
    borough: "Wandsworth",
    region: "South",
    postcodes: ["SW15"],
    tubeOrRail: ["Putney", "East Putney", "Putney Bridge"],
    intro: "Putney sits on the south bank of the Thames with the famous Putney Bridge, a busy high street and the start of the Boat Race.",
    housing: "Mansion-block flats and Edwardian terraces popular with young professionals and families.",
    landmarks: ["Putney Bridge", "Wandsworth Park", "Putney Common"],
    neighbours: ["fulham", "wimbledon", "wandsworth", "richmond"],
  },
  {
    slug: "wimbledon",
    name: "Wimbledon",
    borough: "Merton",
    region: "South",
    postcodes: ["SW19"],
    tubeOrRail: ["Wimbledon", "Wimbledon Park", "South Wimbledon"],
    intro: "Wimbledon is famous for the Championships and combines a busy town centre with leafy Wimbledon Village and the Common.",
    housing: "Family houses near the Common and modern flats near the station — regular moving, mounting and assembly work.",
    landmarks: ["Wimbledon Common", "AELTC Tennis", "Cannizaro Park"],
    neighbours: ["kingston", "putney", "tooting", "new-malden"],
  },
  {
    slug: "clapham",
    name: "Clapham",
    borough: "Lambeth",
    region: "South",
    postcodes: ["SW4", "SW11"],
    tubeOrRail: ["Clapham Common", "Clapham North", "Clapham South"],
    intro: "Clapham centres on Clapham Common and the lively Old Town, popular with young professionals and shared houses.",
    housing: "Lots of HMO conversions and Victorian terraces — heavy demand for furniture assembly and small repairs.",
    landmarks: ["Clapham Common", "Battersea Park", "Old Town", "Venn Street"],
    neighbours: ["battersea", "brixton", "wandsworth", "tooting"],
  },
  {
    slug: "hackney",
    name: "Hackney",
    borough: "Hackney",
    region: "East",
    postcodes: ["E8", "E9", "E5"],
    tubeOrRail: ["Hackney Central", "Hackney Downs", "London Fields"],
    intro: "Hackney is one of east London's most creative boroughs, with London Fields, Broadway Market and Victoria Park drawing renters and new families.",
    housing: "Warehouse conversions, ex-LA flats and Georgian terraces — varied wall types so technique matters for safe TV mounting.",
    landmarks: ["London Fields", "Broadway Market", "Victoria Park", "Hackney Empire"],
    neighbours: ["islington", "dalston", "bethnal-green", "stoke-newington"],
  },
  {
    slug: "islington",
    name: "Islington",
    borough: "Islington",
    region: "North",
    postcodes: ["N1", "N5", "N7"],
    tubeOrRail: ["Angel", "Highbury & Islington", "Caledonian Road"],
    intro: "Islington spans the Angel, Upper Street and Highbury, mixing Georgian squares, canal-side flats and a thriving restaurant scene.",
    housing: "Heritage-listed terraces and canal-side new builds — period properties need careful drilling.",
    landmarks: ["Upper Street", "Regent's Canal", "Highbury Fields", "Almeida Theatre"],
    neighbours: ["hackney", "camden", "kings-cross", "stoke-newington"],
  },
  {
    slug: "camden",
    name: "Camden",
    borough: "Camden",
    region: "North",
    postcodes: ["NW1", "NW3"],
    tubeOrRail: ["Camden Town", "Chalk Farm", "Mornington Crescent"],
    intro: "Camden is famous for its markets, music venues and canal-side life around Camden Lock and Primrose Hill.",
    housing: "Mansion blocks, ex-council flats and canal-side conversions — popular with renters who move frequently.",
    landmarks: ["Camden Market", "Regent's Park", "Primrose Hill", "Roundhouse"],
    neighbours: ["islington", "kings-cross", "hampstead", "kentish-town"],
  },
  {
    slug: "greenwich",
    name: "Greenwich",
    borough: "Greenwich",
    region: "East",
    postcodes: ["SE10"],
    tubeOrRail: ["Greenwich", "Cutty Sark", "Maze Hill"],
    intro: "Greenwich is a riverside east London neighbourhood famous for the Royal Observatory, the Cutty Sark and Greenwich Market.",
    housing: "Period townhouses, riverside developments and ex-LA blocks — varied stock for handyman work.",
    landmarks: ["Royal Observatory", "Greenwich Park", "Cutty Sark", "Greenwich Market"],
    neighbours: ["deptford", "blackheath", "lewisham", "isle-of-dogs"],
  },
];

export function getAreaBySlug(slug: string): LondonArea | undefined {
  return LONDON_AREAS.find((a) => a.slug === slug.toLowerCase());
}

export function areaSlugs(): string[] {
  return LONDON_AREAS.map((a) => a.slug);
}
